<template>
  <div class="oda-root">

    <!-- range chips in the same od-chip voice as the log view's filters, so
         the two views' controls read as one set. 30/90/365 map straight to
         the api's days param; short windows demo too sparse so 7/14d are
         omitted deliberately. -->
    <div class="oda-toolbar od-no-print">
      <div class="oda-range-group" role="group" aria-label="Date range">
        <span class="oda-range-label">Range</span>
        <button
          v-for="r in ranges"
          :key="r.days"
          type="button"
          class="oda-chip"
          :class="{ 'oda-chip--active': rangeDays === r.days }"
          :aria-pressed="rangeDays === r.days ? 'true' : 'false'"
          @click="setRange(r.days)"
        >{{ r.label }}</button>
      </div>
      <span v-if="payload && payload.truncated" class="oda-truncation">
        <i class="fa fa-info-circle"></i>
        Showing the first {{ payload.scanned }} events of this range.
      </span>
    </div>

    <!-- first load only: on a range change the previous render holds at
         reduced opacity (oda-stale below) instead of flashing a spinner -->
    <div v-if="loading && !payload" class="oda-loading">
      <b-spinner class="align-middle"></b-spinner>
      <span>Crunching the numbers…</span>
    </div>

    <div v-else-if="loadError" class="oda-empty">
      <i class="fa fa-exclamation-triangle"></i>
      <span>Analytics did not load.</span>
      <b-button size="sm" variant="outline-secondary" @click="load">Retry</b-button>
    </div>

    <div v-else-if="payload && !payload.kpis.total" class="oda-empty">
      <i class="fa fa-bar-chart"></i>
      <span>No events in this range.</span>
      <!-- already at 12mo: the button would be a dead control, so it hides -->
      <b-button v-if="rangeDays !== 365" size="sm" variant="outline-secondary" @click="setRange(365)">Try 12 months</b-button>
    </div>

    <div v-else-if="payload" class="oda-content" :class="{ 'oda-stale': loading }">
      <!-- kpi strip in the shared dw-stat-tile language (dashboard-widgets.css
           is global), tinted from the ops accent via the --dw-* derivations
           the wrapper sets below. deltas compare against the prior window of
           the same length (payload.previous); a tile without a prior-window
           equivalent (open now is a snapshot) simply has none.
           issues/100k mi is the FI-1817 placeholder: needs a telematics
           mileage denominator. stays visibly "coming soon" -- never fake. -->
      <div class="oda-kpis dw-stat-grid">
        <div
          v-for="t in kpiTiles"
          :key="'kpi-' + t.key"
          class="dw-stat-tile"
          :class="{ 'oda-kpi--soon': t.soon }"
          :title="t.soon ? 'Requires telematics mileage' : null"
        >
          <div class="dw-stat-tile-label">{{ t.label }}</div>
          <div v-if="t.soon" class="dw-stat-tile-value oda-kpi-soon-value">Coming soon</div>
          <div v-else class="dw-stat-tile-value" :class="t.valueCls">{{ t.value }}</div>
          <div v-if="t.delta" class="oda-delta" :class="t.delta.cls" :title="deltaCaption">
            <i v-if="t.delta.d" :class="t.delta.d > 0 ? 'fa fa-caret-up' : 'fa fa-caret-down'" aria-hidden="true"></i>
            <span>{{ t.delta.text }}</span>
          </div>
        </div>
      </div>

      <!-- the hero: the page's actual story is the trend, so the time chart
           is full-width with a trailing average over the daily noise. built
           as inline svg (not a chart lib) so marks follow the dataviz specs:
           thin stacked columns with 2px surface gaps, rounded data-ends,
           hairline solid gridlines, a crosshair tooltip, and a table twin. -->
      <div class="oda-card oda-card--hero">
        <div class="oda-card-header">
          <span class="oda-card-title"><i class="fa fa-bar-chart" aria-hidden="true"></i> Events over time</span>
          <span class="oda-legend">
            <span class="oda-legend-item"><span class="oda-swatch oda-swatch--incident"></span>Issues</span>
            <span class="oda-legend-item"><span class="oda-swatch oda-swatch--kudos"></span>Kudos</span>
            <span class="oda-legend-item"><span class="oda-swatch oda-swatch--note"></span>Notes</span>
            <span class="oda-legend-item"><span class="oda-linekey"></span>{{ avgLabel }}</span>
          </span>
          <span class="oda-mode-chips" role="group" aria-label="View">
            <button type="button" class="oda-chip oda-chip--sm"
                    :class="{ 'oda-chip--active': heroView === 'chart' }"
                    :aria-pressed="heroView === 'chart' ? 'true' : 'false'"
                    @click="heroView = 'chart'">Chart</button>
            <button type="button" class="oda-chip oda-chip--sm"
                    :class="{ 'oda-chip--active': heroView === 'table' }"
                    :aria-pressed="heroView === 'table' ? 'true' : 'false'"
                    @click="heroView = 'table'">Table</button>
          </span>
        </div>
        <div class="oda-card-body">
          <!-- role=group, not img: img would flatten the descendants and a
               screen reader could never hear the values the aria-label
               promises; the live region below voices each arrow-key step -->
          <div
            v-if="heroView === 'chart'"
            ref="heroBody"
            class="oda-tl-wrap"
            tabindex="0"
            role="group"
            :aria-label="heroAria"
            @keydown.left.prevent="nudgeHover(-1)"
            @keydown.right.prevent="nudgeHover(1)"
            @keydown.esc="hoverIdx = null"
            @blur="hoverIdx = null"
            @mouseleave="hoverIdx = null"
          >
            <span class="sr-only" aria-live="polite">{{ tipText }}</span>
            <svg :width="chartW" :height="chartH">
              <g>
                <line v-for="t in yTicks" :key="'g' + t.v" class="oda-tl-grid"
                      :x1="heroM.l" :x2="chartW - heroM.r" :y1="t.y" :y2="t.y" />
                <text v-for="t in yTicks" :key="'y' + t.v" class="oda-tl-tick"
                      :x="heroM.l - 8" :y="t.y + 3.5" text-anchor="end">{{ t.v.toLocaleString() }}</text>
              </g>
              <g v-for="(b, i) in heroBuckets" :key="'b' + b.bucket"
                 :class="{ 'oda-tl-lift': hoverIdx === i }">
                <path v-for="seg in b.segs" :key="seg.k" :d="seg.d"
                      :class="'oda-tl-seg oda-tl-seg--' + seg.k" />
              </g>
              <path v-if="avgPath" :d="avgPath" class="oda-tl-avg" />
              <g>
                <text v-for="b in xLabels" :key="'x' + b.bucket" class="oda-tl-tick"
                      :x="b.x" :y="chartH - 8" text-anchor="middle">{{ b.label }}</text>
              </g>
              <line v-if="hoverIdx !== null && heroBuckets[hoverIdx]" class="oda-tl-cross"
                    :x1="heroBuckets[hoverIdx].cx" :x2="heroBuckets[hoverIdx].cx"
                    :y1="heroM.t" :y2="chartH - heroM.b" />
              <!-- hit targets: the whole column slot, never just the painted bar -->
              <rect v-for="(b, i) in heroBuckets" :key="'h' + b.bucket" class="oda-tl-hit"
                    :x="b.x0" :y="heroM.t" :width="b.slotW" :height="chartH - heroM.t - heroM.b"
                    @mouseenter="hoverIdx = i" />
            </svg>
            <div v-if="tipModel" class="oda-tip" :style="tipStyle">
              <div class="oda-tip-title">{{ tipModel.title }}</div>
              <div v-for="row in tipModel.rows" :key="row.label" class="oda-tip-row"
                   :class="{ 'oda-tip-row--rule': row.rule }">
                <span class="oda-tip-key" :class="row.k ? 'oda-tip-key--' + row.k : ''"></span>
                <span class="oda-tip-val">{{ row.v }}</span>
                <span class="oda-tip-lbl">{{ row.label }}</span>
              </div>
            </div>
          </div>
          <!-- tabindex: safari does not make overflow scrollers keyboard
               focusable on its own, and this table is the chart's
               accessibility twin -->
          <div v-else class="oda-tv-scroll" tabindex="0" role="region" aria-label="Events over time, table view">
            <table class="oda-tv">
              <thead>
                <tr>
                  <th>{{ bucketNoun }}</th>
                  <th>Issues</th><th>Kudos</th><th>Notes</th><th>Total</th><th>{{ avgLabel }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(b, i) in overTime" :key="'tv' + b.bucket">
                  <td>{{ bucketLabel(b.bucket) }}</td>
                  <td>{{ b.incident }}</td><td>{{ b.kudos }}</td><td>{{ b.note }}</td>
                  <td>{{ b.incident + b.kudos + b.note }}</td>
                  <td>{{ avgSeries[i] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ranked lists: label + thin value bar + count, a table/chart hybrid.
           the count is always visible as text, so color never gates a value
           (that is also the contrast relief the palette validator requires
           for the de-emphasis gray). -->
      <div class="oda-grid">
        <div v-for="card in rankedCards" :key="card.key" class="oda-card">
          <div class="oda-card-header">
            <span class="oda-card-title"><i :class="'fa ' + card.icon" aria-hidden="true"></i> {{ card.title }}</span>
            <!-- two series only in the driver card's combined mode; a single
                 series carries no legend box (the title already names it) -->
            <span v-if="card.key === 'driver' && driverMode === 'all'" class="oda-legend">
              <span class="oda-legend-item"><span class="oda-swatch oda-swatch--incident"></span>Issues</span>
              <span class="oda-legend-item"><span class="oda-swatch oda-swatch--kudos"></span>Kudos</span>
            </span>
            <!-- per-type lens; rankings come from the server per mode so a
                 kudos leader outside the by-total top n still ranks -->
            <span v-if="card.key === 'driver'" class="oda-mode-chips" role="group" aria-label="Event type">
              <button v-for="m in driverModeOptions" :key="'dm-' + m.value" type="button"
                      class="oda-chip oda-chip--sm"
                      :class="{ 'oda-chip--active': driverMode === m.value }"
                      :aria-pressed="driverMode === m.value ? 'true' : 'false'"
                      @click="driverMode = m.value">{{ m.label }}</button>
            </span>
          </div>
          <div class="oda-card-body">
            <div v-if="card.rows.length" class="oda-rank">
              <div v-for="row in card.rows" :key="row.label" class="oda-rank-row" :title="row.tip">
                <!-- sr-only twin of the title tooltip: the issues/kudos split
                     must reach keyboard and touch readers too -->
                <span class="oda-rank-label">{{ row.label }}<span class="sr-only">, {{ row.tip }}</span></span>
                <span class="oda-rank-track">
                  <span class="oda-rank-bar" :style="{ width: row.pct + '%' }">
                    <span v-for="seg in row.segs" :key="seg.k" class="oda-rank-seg"
                          :class="'oda-rank-seg--' + seg.k" :style="{ flexGrow: seg.v }"></span>
                  </span>
                </span>
                <span class="oda-rank-count">{{ row.total.toLocaleString() }}</span>
              </div>
            </div>
            <div v-else class="oda-card-empty">No data available</div>
          </div>
        </div>
      </div>

      <!-- composition mixes: a pie compares close values poorly, so each mix
           is a single stacked band plus a text legend carrying count + share.
           segment order inside each band is part of the validated palette
           (sources deliberately seats gray between blue and purple). -->
      <div class="oda-band-grid">
        <div v-for="card in bandCards" :key="card.key" class="oda-card">
          <div class="oda-card-header">
            <span class="oda-card-title"><i :class="'fa ' + card.icon" aria-hidden="true"></i> {{ card.title }}</span>
          </div>
          <div class="oda-card-body">
            <template v-if="card.total">
              <div class="oda-band" role="img" :aria-label="card.aria">
                <span v-for="seg in card.segs.filter(s => s.v > 0)" :key="seg.k"
                      class="oda-band-seg" :class="'oda-band-seg--' + seg.k"
                      :style="{ flexGrow: seg.v }"
                      :title="seg.label + ': ' + seg.v.toLocaleString() + ' (' + seg.pct + '%)'"></span>
              </div>
              <div class="oda-band-legend">
                <div v-for="seg in card.segs" :key="'l' + seg.k" class="oda-band-lrow"
                     :class="{ 'oda-band-lrow--zero': !seg.v }">
                  <span class="oda-swatch" :class="'oda-swatch--' + seg.k"></span>
                  <span class="oda-band-lbl">{{ seg.label }}</span>
                  <span class="oda-band-cnt">{{ seg.v.toLocaleString() }}</span>
                  <span class="oda-band-pct">{{ seg.pct }}%</span>
                </div>
              </div>
            </template>
            <div v-else class="oda-card-empty">No data available</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get_ops_desk_analytics } from '../networking/apis'

// chart colors share the list's chip hue families (issue orange, kudos green)
// but each step is validated with the dataviz palette checker rather than
// copied from the chips verbatim: the chip green (#1fab42) sits at ΔE 5.1
// from the issue orange under deuteranopia, so the kudos mark shifts to a
// teal-green step (#0b7f56, ΔE 8.4) that survives red-green color blindness.
// the midnight values in the unscoped block below are separately validated
// against the dark card surface -- dark mode is selected, never auto-flipped.
// grays are deliberate de-emphasis slots; their sub-3:1 contrast is relieved
// by every value also appearing as text (rank counts, band legends, table).

const PREVENT_META = {
  preventable: { k: 'p-prev', label: 'Preventable' },
  not_preventable: { k: 'p-notprev', label: 'Not preventable' },
  absence: { k: 'p-absence', label: 'Absence' },
  recognition: { k: 'p-recog', label: 'Recognition' },
  unclassified: { k: 'p-uncls', label: 'Unclassified' },
}

export default {
  name: 'ops_desk_analytics',

  props: {
    tenant: { type: String, required: true },
    // presentational only: the api independently gates the preventability
    // block by identity; this just avoids a card flash for non-admins
    isAdminView: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      loadError: false,
      payload: null,
      // events-by-driver type lens: all | incident | kudos
      driverMode: 'all',
      // hero card: chart | table (the accessibility twin of the svg)
      heroView: 'chart',
      hoverIdx: null,
      chartW: 960,
      rangeDays: 90,
      ranges: [
        { days: 30, label: '30d' },
        { days: 90, label: '90d' },
        { days: 365, label: '12mo' },
      ],
    }
  },

  computed: {
    openNow() {
      const status = (this.payload && this.payload.all_time && this.payload.all_time.status) || {}
      return (status.new || 0) + (status.in_progress || 0) + (status.awaiting_followup || 0)
    },
    overTime() {
      return (this.payload && this.payload.over_time) || []
    },
    isWeekly() {
      return !!(this.payload && this.payload.window.granularity === 'week')
    },
    bucketNoun() {
      return this.isWeekly ? 'Week' : 'Day'
    },
    avgLabel() {
      return this.isWeekly ? '4-week avg' : '7-day avg'
    },
    deltaCaption() {
      const days = (this.payload && this.payload.window.days) || 0
      return 'vs prior ' + days + ' days'
    },
    kpiTiles() {
      const k = this.payload.kpis
      return [
        { key: 'total', label: 'Events', value: k.total.toLocaleString(), delta: this.delta('total', 'neutral') },
        { key: 'open', label: 'Open now', value: this.openNow.toLocaleString() },
        // no delta on resolved: the prior cohort has had strictly more time
        // to reach resolved, so the comparison reads as a permanent decline
        // on a healthy tenant (the api ships no prior resolved count either)
        { key: 'resolved', label: 'Resolved', value: k.resolved_in_window.toLocaleString(), valueCls: 'dw-stat-tile-value--good' },
        { key: 'kudos', label: 'Kudos', value: k.kudos.toLocaleString(), valueCls: 'dw-stat-tile-value--good', delta: this.delta('kudos', 'up-good') },
        { key: 'delayed', label: 'Customer delayed', value: k.customer_delayed_yes.toLocaleString(), valueCls: 'oda-kpi-warn', delta: this.delta('customer_delayed_yes', 'up-bad') },
        { key: 'rate', label: 'Events / week', value: String(k.events_per_week) },
        { key: 'mileage', label: 'Issues / 100k mi', soon: true },
      ]
    },

    // ---- hero timeline geometry ----
    heroM() {
      return { l: 46, r: 14, t: 12, b: 26 }
    },
    chartH() {
      return 300
    },
    bucketTotals() {
      return this.overTime.map(b => b.incident + b.kudos + b.note)
    },
    // trailing mean over the last 7 day-buckets (4 for weekly windows); the
    // line answers "which way is this going" over the daily spikes
    avgSeries() {
      const w = this.isWeekly ? 4 : 7
      const t = this.bucketTotals
      return t.map((_, i) => {
        const from = Math.max(0, i - w + 1)
        const slice = t.slice(from, i + 1)
        return Math.round((slice.reduce((a, b) => a + b, 0) / slice.length) * 10) / 10
      })
    },
    yMax() {
      const raw = Math.max(1, ...this.bucketTotals)
      // events are integers, so the axis never steps finer than 1
      const step = Math.max(1, this.niceStep(raw / 4))
      return { step, max: Math.ceil(raw / step) * step }
    },
    yTicks() {
      const { step, max } = this.yMax
      const ticks = []
      for (let v = 0; v <= max; v += step) ticks.push({ v, y: this.yFor(v) })
      return ticks
    },
    heroBuckets() {
      const m = this.heroM
      const n = this.overTime.length
      if (!n) return []
      const plotW = Math.max(50, this.chartW - m.l - m.r)
      const slotW = plotW / n
      const barW = Math.max(2, Math.min(24, slotW - Math.max(2, slotW * 0.25)))
      const baseY = this.chartH - m.b
      return this.overTime.map((b, i) => {
        const x0 = m.l + slotW * i
        const x = x0 + (slotW - barW) / 2
        const order = [
          { k: 'incident', v: b.incident },
          { k: 'kudos', v: b.kudos },
          { k: 'note', v: b.note },
        ].filter(s => s.v > 0)
        const topIdx = order.length - 1
        let bottom = baseY
        const segs = order.map((s, si) => {
          const h = baseY - this.yFor(s.v)
          const yTop = bottom - h
          // 2px surface gap between touching segments (never a stroke); the
          // gap comes out of the upper segment so the baseline stays square
          const gap = si > 0 ? 2 : 0
          const drawH = Math.max(h - gap, h > 0 ? 1 : 0)
          const drawBottom = bottom - gap
          const d = si === topIdx
            ? this.roundedTopPath(x, drawBottom - drawH, barW, drawH)
            : 'M' + x + ',' + (drawBottom - drawH) + 'h' + barW + 'v' + drawH + 'h-' + barW + 'Z'
          bottom = yTop
          return { k: s.k, d }
        })
        return { bucket: b.bucket, x0, slotW, cx: x0 + slotW / 2, segs }
      })
    },
    avgPath() {
      if (this.heroBuckets.length < 2) return ''
      return this.heroBuckets
        .map((b, i) => (i ? 'L' : 'M') + b.cx.toFixed(1) + ',' + this.yFor(this.avgSeries[i]).toFixed(1))
        .join(' ')
    },
    hTickEvery() {
      // thin by available width, not just bucket count: a "May 13" label
      // needs ~44px of slot, so a phone shows ~7 labels where a desktop
      // shows 12 -- otherwise 90 daily buckets collide at 375px
      const m = this.heroM
      const plotW = Math.max(50, this.chartW - m.l - m.r)
      const maxLabels = Math.max(3, Math.min(12, Math.floor(plotW / 44)))
      return Math.max(1, Math.ceil(this.overTime.length / maxLabels))
    },
    xLabels() {
      return this.heroBuckets
        .filter((_, i) => i % this.hTickEvery === 0)
        .map(b => ({ bucket: b.bucket, x: b.cx, label: this.bucketLabel(b.bucket) }))
    },
    heroAria() {
      const w = (this.payload && this.payload.window) || {}
      return 'Stacked ' + (this.isWeekly ? 'weekly' : 'daily') + ' issues, kudos, and notes with a trailing average, ' +
        String(w.from || '').slice(0, 10) + ' to ' + String(w.to || '').slice(0, 10) +
        '. Use arrow keys to step through values, or switch to the table view.'
    },
    tipModel() {
      const i = this.hoverIdx
      const b = i !== null ? this.overTime[i] : null
      if (!b) return null
      return {
        title: (this.isWeekly ? 'Week of ' : '') + this.bucketLabel(b.bucket),
        rows: [
          { k: 'incident', label: 'Issues', v: b.incident },
          { k: 'kudos', label: 'Kudos', v: b.kudos },
          { k: 'note', label: 'Notes', v: b.note },
          { label: 'Total', v: b.incident + b.kudos + b.note, rule: true },
          { k: 'avg', label: this.avgLabel, v: this.avgSeries[i] },
        ],
      }
    },
    // spoken twin of the tooltip for the aria-live region
    tipText() {
      const t = this.tipModel
      if (!t) return ''
      return t.title + ': ' + t.rows.map(r => r.v + ' ' + r.label).join(', ')
    },
    tipStyle() {
      const b = this.hoverIdx !== null ? this.heroBuckets[this.hoverIdx] : null
      if (!b) return {}
      const flip = b.cx > this.chartW / 2
      return flip
        ? { left: (b.cx - 12) + 'px', transform: 'translateX(-100%)' }
        : { left: (b.cx + 12) + 'px' }
    },

    // ---- ranked lists ----
    driverModeOptions() {
      return [
        { value: 'all', label: 'All' },
        { value: 'incident', label: 'Issues' },
        { value: 'kudos', label: 'Kudos' },
      ]
    },
    rankedCards() {
      const p = this.payload
      if (!p) return []
      const cards = []

      const cat = (p.by_category || []).slice(0, 10)
      cards.push({
        key: 'category',
        title: 'Events by category',
        icon: 'fa-tags',
        rows: this.rankRows(cat.map(r => ({
          label: this.readable(r.category),
          segs: [{ k: 'accent', v: r.count }],
          tip: r.count.toLocaleString() + ' events',
        }))),
      })

      let dRows
      if (this.driverMode !== 'all') {
        // per-mode rankings ship from the server (top_drivers_by): slicing
        // the combined top list here would hide a kudos leader who never
        // cracks the by-total cut. the filter fallback only covers an older
        // api payload without the keyed lists.
        const field = this.driverMode === 'incident' ? 'issues' : 'kudos'
        const keyed = p.top_drivers_by || {}
        const rows = keyed[this.driverMode] || (p.top_drivers || []).filter(r => r[field])
        const segK = this.driverMode
        dRows = rows.map(r => ({
          label: r.driver,
          segs: [{ k: segK, v: r[field] }],
          tip: r[field].toLocaleString() + (field === 'issues' ? ' issues' : ' kudos'),
        }))
      } else {
        dRows = (p.top_drivers || []).map(r => ({
          label: r.driver,
          segs: [{ k: 'incident', v: r.issues }, { k: 'kudos', v: r.kudos }].filter(s => s.v > 0),
          tip: r.issues.toLocaleString() + ' issues, ' + r.kudos.toLocaleString() + ' kudos',
        }))
      }
      cards.push({ key: 'driver', title: 'Events by driver', icon: 'fa-user', rows: this.rankRows(dRows) })

      cards.push({
        key: 'vehicle',
        title: 'Events by vehicle',
        icon: 'fa-bus',
        rows: this.rankRows((p.top_vehicles || []).map(r => ({
          label: String(r.vehicle),
          segs: [{ k: 'accent', v: r.count }],
          tip: r.count.toLocaleString() + ' events',
        }))),
      })

      if (p.preventability && this.isAdminView) {
        // the api already gates the block by identity; the prop keeps the
        // card from flashing if a shared payload ever reaches a non-admin.
        // ranked rows rather than a band: the semantic status colors (red /
        // teal / amber) cannot pass adjacency cvd checks side by side in the
        // dark theme, and rows never touch, so each keeps its meaning
        const rows = Object.entries(p.preventability)
          .filter(([, n]) => n > 0)
          .sort((a, b) => b[1] - a[1])
          .map(([key, n]) => {
            const meta = PREVENT_META[key] || { k: 'p-uncls', label: this.readable(key) }
            return { label: meta.label, segs: [{ k: meta.k, v: n }], tip: n.toLocaleString() + ' events' }
          })
        cards.push({ key: 'preventability', title: 'Preventability', icon: 'fa-shield', rows: this.rankRows(rows) })
      }
      return cards
    },

    // ---- composition bands ----
    bandCards() {
      const p = this.payload
      if (!p) return []
      return [
        this.mkBand('status', 'Status', 'fa-tasks', p.status_mix || {}, [
          { key: 'new', k: 's-new', label: 'New' },
          { key: 'in_progress', k: 's-progress', label: 'In progress' },
          { key: 'awaiting_followup', k: 's-waiting', label: 'Awaiting follow-up' },
          { key: 'resolved', k: 's-resolved', label: 'Resolved' },
        ]),
        this.mkBand('severity', 'Severity', 'fa-exclamation-triangle', p.severity_mix || {}, [
          { key: 'high', k: 'v-high', label: 'High' },
          { key: 'normal', k: 'v-normal', label: 'Normal' },
          { key: 'low', k: 'v-low', label: 'Low' },
        ]),
        this.mkBand('delayed', 'Customer delayed', 'fa-clock-o', p.customer_delayed || {}, [
          { key: 'yes', k: 'incident', label: 'Yes' },
          { key: 'no', k: 'kudos', label: 'No' },
          { key: 'not_applicable', k: 'note', label: 'Not applicable' },
          { key: 'unspecified', k: 'd-unspec', label: 'Unspecified' },
        ]),
        this.mkBand('sources', 'Submissions by source', 'fa-inbox', p.sources || {}, [
          { key: 'staff', k: 'src-staff', label: 'Staff' },
          { key: 'imported', k: 'src-imported', label: 'Imported / Sheet sync' },
          { key: 'public_form', k: 'src-public', label: 'Public form' },
        ]),
      ]
    },
  },

  watch: {
    tenant() { this.load() },
    heroView() { this.$nextTick(() => this.measure()) },
  },

  mounted() {
    this.load()
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.measure())
      this._ro.observe(this.$el)
    } else {
      this._onResize = () => this.measure()
      window.addEventListener('resize', this._onResize)
    }
  },

  updated() {
    this.measure()
  },

  beforeDestroy() {
    if (this._ro) this._ro.disconnect()
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },

  methods: {
    readable(v) {
      return String(v || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },
    bucketLabel(iso) {
      // "2026-07-03" -> "Jul 3"; keeps the axis compact
      const d = new Date(iso + 'T00:00:00')
      return isNaN(d) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    measure() {
      // guard against the updated() loop: only assign on a real change
      const el = this.$refs.heroBody
      if (el && el.clientWidth && el.clientWidth !== this.chartW) this.chartW = el.clientWidth
    },
    niceStep(raw) {
      // snap an axis step to 1/2/5 x 10^k so tick values read clean
      const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 0.1))))
      for (const m of [1, 2, 5, 10]) {
        if (raw <= m * mag) return m * mag
      }
      return 10 * mag
    },
    yFor(v) {
      const m = this.heroM
      const plotH = this.chartH - m.t - m.b
      return this.chartH - m.b - (v / this.yMax.max) * plotH
    },
    roundedTopPath(x, y, w, h, r) {
      // rounded data-end (top corners only); the baseline end stays square
      const rr = Math.min(r || 3, w / 2, h)
      if (rr <= 0.5) return 'M' + x + ',' + y + 'h' + w + 'v' + h + 'h-' + w + 'Z'
      return 'M' + x + ',' + (y + h) +
        'L' + x + ',' + (y + rr) +
        'Q' + x + ',' + y + ' ' + (x + rr) + ',' + y +
        'L' + (x + w - rr) + ',' + y +
        'Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + rr) +
        'L' + (x + w) + ',' + (y + h) + 'Z'
    },
    nudgeHover(step) {
      const n = this.heroBuckets.length
      if (!n) return
      const cur = this.hoverIdx === null ? (step > 0 ? -1 : n) : this.hoverIdx
      this.hoverIdx = Math.max(0, Math.min(n - 1, cur + step))
    },
    delta(key, sense) {
      const prev = this.payload && this.payload.previous
      if (!prev || !prev.kpis || typeof prev.kpis[key] !== 'number') return null
      // a truncated prior drain undercounts; a delta against it would lie
      if (prev.truncated) return null
      const d = this.payload.kpis[key] - prev.kpis[key]
      let cls = 'oda-delta--flat'
      if (d !== 0 && sense !== 'neutral') {
        cls = (d > 0) === (sense === 'up-good') ? 'oda-delta--good' : 'oda-delta--bad'
      }
      return { d, cls, text: d > 0 ? '+' + d.toLocaleString() : d < 0 ? d.toLocaleString() : '±0' }
    },
    rankRows(items) {
      const max = Math.max(1, ...items.map(r => r.segs.reduce((s, x) => s + x.v, 0)))
      return items.map(r => {
        const total = r.segs.reduce((s, x) => s + x.v, 0)
        return { ...r, total, pct: Math.max(1.5, (total / max) * 100) }
      })
    },
    mkBand(key, title, icon, mix, defs) {
      const total = defs.reduce((s, d) => s + (mix[d.key] || 0), 0)
      const segs = defs.map(d => {
        const v = mix[d.key] || 0
        return { k: d.k, label: d.label, v, pct: total ? Math.round((v * 100) / total) : 0 }
      })
      return {
        key,
        title,
        icon,
        total,
        segs,
        aria: title + ': ' + segs.map(s => s.label + ' ' + s.v).join(', '),
      }
    },
    setRange(days) {
      if (days === this.rangeDays && this.payload) return
      this.rangeDays = days
      this.load()
    },
    async load() {
      this.loading = true
      this.loadError = false
      const seq = (this._seq = (this._seq || 0) + 1)
      try {
        // envelope shape matches the list route: value.data is the payload
        const { value: { data = null } = {} } = await get_ops_desk_analytics(this.tenant, { days: this.rangeDays }) || {}
        if (seq !== this._seq) return
        if (!data || !data.kpis) throw new Error('empty analytics payload')
        this.payload = data
        this.hoverIdx = null
        this.$nextTick(() => this.measure())
      } catch (e) {
        if (seq !== this._seq) return
        this.loadError = true
        this.payload = null
      } finally {
        if (seq === this._seq) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.oda-root {
  margin-top: 4px;
  /* the rank bars, bands, and swatches ARE the data; without this the
     ops-desk print stylesheet strips their backgrounds to white (inherited
     property, print-only effect) */
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  /* single accent feeding the card chrome + kpi tints, so the analytics view
     wears the widgets-dashboard language in the ops family's own color */
  --oda-accent: var(--fi-ops-accent, #3a6e99);
  /* light-mode series palette, validated with the dataviz six-checks script
     against the tinted card surface (#f3f7f9). midnight re-resolves every
     one of these in the unscoped block at the bottom of the file. */
  --oda-c-incident: #d9730d;
  --oda-c-kudos: #0b7f56;
  --oda-c-note: #9aa4af;
  --oda-c-avg: #33414f;
  --oda-s-new: #6b7280;
  --oda-s-progress: #1668d6;
  --oda-s-waiting: #d39410;
  --oda-s-resolved: #0b7f56;
  --oda-v-high: #d62f2f;
  --oda-v-normal: #2b6cb8;
  --oda-v-low: #9aa4af;
  --oda-d-unspec: #cfd8e1;
  --oda-src-staff: #2b6cb8;
  --oda-src-public: #8a3fb5;
  --oda-src-imported: #9aa4af;
  --oda-p-prev: #d62f2f;
  --oda-p-notprev: #0b7f56;
  --oda-p-absence: #d39410;
  --oda-p-recog: #2b6cb8;
  --oda-p-uncls: #9aa4af;
  --oda-grid-line: #e9eef3;
}

.oda-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/* same pill values as ops_desk.vue's .od-chip / .od-chip-group-label */
.oda-range-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.oda-range-label {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--fi-text-muted, #6b7280);
  margin-right: 2px;
}
.oda-chip {
  background: #f8fafc;
  border: 1px solid #d6dde6;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.oda-chip:hover:not(.oda-chip--active) { background: #eef4fb; border-color: #b8c8da; }
.oda-chip--active {
  background: var(--fi-ops-accent, #2b6cb8);
  border-color: var(--fi-ops-accent, #2b6cb8);
  color: #fff;
}
.oda-chip:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 1px; }
/* compact in-card variant for the per-type lens + chart/table toggles */
.oda-mode-chips { display: inline-flex; gap: 4px; margin-left: auto; }
.oda-chip--sm { padding: 2px 9px; font-size: 11.5px; }

.oda-truncation {
  font-size: 12.5px;
  color: #7a5400;
  background: #fff4d1;
  border: 1px solid #eadfae;
  border-radius: 8px;
  padding: 4px 10px;
}

.oda-loading, .oda-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 64px 0;
  color: var(--fi-text-muted, #6b7a8c);
  font-size: 15px;
}
.oda-empty .fa { font-size: 22px; opacity: 0.6; }

/* refetch keeps the frame: previous render dims instead of a spinner flash */
.oda-stale {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity .15s;
}

/* the dw-stat-tile family reads these derivations exactly as it does inside
   a dashboard widget's .mdash-card-body (see dashboard-widgets.css) */
.oda-kpis {
  --dw-accent: var(--oda-accent);
  --dw-tint: color-mix(in srgb, var(--dw-accent) 8%, var(--fi-surface));
  --dw-tint-strong: color-mix(in srgb, var(--dw-accent) 15%, var(--fi-surface));
  --dw-edge: color-mix(in srgb, var(--dw-accent) 26%, var(--fi-line));
  margin-bottom: 16px;
}
.oda-kpi-warn { color: #b85a1e; }
.oda-kpi--soon { opacity: 0.65; }
.oda-kpi-soon-value { font-size: 14px; padding: 5px 0 3px; color: var(--fi-text-muted, #6b7a8c); }

/* signed vs-prior-window movement; hue = direction x whether up is good */
.oda-delta {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 1px;
  font-size: 11.5px;
  font-weight: 600;
}
.oda-delta--good { color: var(--fi-success, #2f7d32); }
.oda-delta--bad { color: #b85a1e; }
.oda-delta--flat { color: var(--fi-text-muted, #8a97a5); }

.oda-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 16px;
}
/* the mix cards are short, so they pack tighter than the ranked lists */
.oda-band-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 16px;
}
@media (max-width: 900px) {
  .oda-grid, .oda-band-grid { grid-template-columns: 1fr; }
}

/* widget-language card chrome, cloned from alerts_dashboard's .ad-card
   (itself the .mdash-card banding) with the ops accent substituted */
.oda-card {
  display: flex;
  flex-direction: column;
  background: var(--fi-surface, #fff);
  border: 2px solid color-mix(in srgb, var(--oda-accent) 28%, var(--fi-line));
  border-radius: 16px;
  box-shadow:
    0 4px 22px color-mix(in srgb, var(--oda-accent) 14%, transparent),
    0 1px 4px rgba(20, 40, 60, 0.06);
  position: relative;
  overflow: hidden;
}
.oda-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: linear-gradient(
    90deg,
    var(--oda-accent),
    color-mix(in srgb, var(--oda-accent) 55%, white)
  );
  z-index: 3;
}
.oda-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(
    180deg,
    var(--oda-accent),
    color-mix(in srgb, var(--oda-accent) 70%, white)
  );
  z-index: 1;
}
.oda-card--hero { margin-bottom: 16px; }
.oda-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 13px 16px 11px 20px;
  background: color-mix(in srgb, var(--oda-accent) 14%, var(--fi-surface));
  border-bottom: 2px solid color-mix(in srgb, var(--oda-accent) 22%, var(--fi-line));
  position: relative;
  z-index: 2;
}
.oda-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: color-mix(in srgb, var(--oda-accent) 65%, var(--fi-text));
}
.oda-card-title .fa {
  color: var(--oda-accent);
}
.oda-card-body {
  flex: 1;
  padding: 12px 14px 10px 18px;
  background: color-mix(in srgb, var(--oda-accent) 6%, var(--fi-surface));
  position: relative;
  z-index: 2;
}
.oda-card-empty {
  text-align: center;
  color: var(--fi-text-muted, #8a97a5);
  padding: 48px 0;
}

/* series identity: swatch mirrors the mark (rect for fills, line for lines);
   text stays in text tokens, never the series color */
.oda-legend {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.oda-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #6b7a8c);
}
.oda-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: none;
}
.oda-linekey {
  display: inline-block;
  width: 14px;
  height: 2px;
  border-radius: 2px;
  background: var(--oda-c-avg);
}

.oda-swatch--incident { background: var(--oda-c-incident); }
.oda-swatch--kudos { background: var(--oda-c-kudos); }
.oda-swatch--note { background: var(--oda-c-note); }
.oda-swatch--s-new { background: var(--oda-s-new); }
.oda-swatch--s-progress { background: var(--oda-s-progress); }
.oda-swatch--s-waiting { background: var(--oda-s-waiting); }
.oda-swatch--s-resolved { background: var(--oda-s-resolved); }
.oda-swatch--v-high { background: var(--oda-v-high); }
.oda-swatch--v-normal { background: var(--oda-v-normal); }
.oda-swatch--v-low { background: var(--oda-v-low); }
.oda-swatch--d-unspec { background: var(--oda-d-unspec); }
.oda-swatch--src-staff { background: var(--oda-src-staff); }
.oda-swatch--src-public { background: var(--oda-src-public); }
.oda-swatch--src-imported { background: var(--oda-src-imported); }

/* ---- hero timeline ---- */
.oda-tl-wrap {
  position: relative;
  outline: none;
}
.oda-tl-wrap:focus-visible {
  border-radius: 8px;
  box-shadow: 0 0 0 2px var(--oda-accent);
}
.oda-tl-grid { stroke: var(--oda-grid-line); stroke-width: 1; }
.oda-tl-tick {
  fill: var(--fi-text-muted, #6b7a8c);
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
}
.oda-tl-seg--incident { fill: var(--oda-c-incident); }
.oda-tl-seg--kudos { fill: var(--oda-c-kudos); }
.oda-tl-seg--note { fill: var(--oda-c-note); }
.oda-tl-lift .oda-tl-seg { filter: brightness(1.12); }
.oda-tl-avg {
  fill: none;
  stroke: var(--oda-c-avg);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.oda-tl-cross {
  stroke: color-mix(in srgb, var(--fi-text-muted, #6b7a8c) 55%, transparent);
  stroke-width: 1;
}
.oda-tl-hit { fill: transparent; }

.oda-tip {
  position: absolute;
  top: 10px;
  z-index: 5;
  min-width: 150px;
  padding: 8px 12px 9px;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.18);
  pointer-events: none;
}
.oda-tip-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--fi-text-muted, #6b7a8c);
  margin-bottom: 4px;
}
.oda-tip-row {
  display: grid;
  grid-template-columns: 14px 34px 1fr;
  gap: 6px;
  align-items: center;
  font-size: 12.5px;
  line-height: 1.55;
}
.oda-tip-row--rule {
  border-top: 1px solid var(--fi-line, #e3e9ef);
  margin-top: 3px;
  padding-top: 3px;
}
/* value leads, label follows: here the reader has the series, wants the number */
.oda-tip-val {
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--fi-text, #2c3a49);
}
.oda-tip-lbl { color: var(--fi-text-muted, #6b7a8c); }
.oda-tip-key { height: 3px; border-radius: 2px; }
.oda-tip-key--incident { background: var(--oda-c-incident); }
.oda-tip-key--kudos { background: var(--oda-c-kudos); }
.oda-tip-key--note { background: var(--oda-c-note); }
.oda-tip-key--avg { background: var(--oda-c-avg); }

.oda-tv-scroll { max-height: 300px; overflow: auto; }
/* separate + box-shadow, not collapse + border: collapsed borders detach
   from a position:sticky th and scroll away with the body */
.oda-tv { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12.5px; }
.oda-tv th {
  position: sticky;
  top: 0;
  background: color-mix(in srgb, var(--oda-accent) 6%, var(--fi-surface));
  text-align: right;
  font-weight: 700;
  color: var(--fi-text-muted, #6b7a8c);
  padding: 4px 10px;
  box-shadow: 0 1px 0 var(--fi-line, #d6dde6);
}
.oda-tv th:first-child { text-align: left; }
.oda-tv td {
  text-align: right;
  font-variant-numeric: tabular-nums;
  padding: 3px 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--fi-line, #e3e9ef) 55%, transparent);
  color: var(--fi-text, #2c3a49);
}
.oda-tv td:first-child { text-align: left; color: var(--fi-text-muted, #6b7a8c); }

/* ---- ranked value bars: label / thin bar / count ---- */
.oda-rank { display: flex; flex-direction: column; gap: 2px; }
.oda-rank-row {
  display: grid;
  grid-template-columns: 150px 1fr 48px;
  align-items: center;
  gap: 10px;
  padding: 4px 6px;
  border-radius: 8px;
}
.oda-rank-row:hover { background: color-mix(in srgb, var(--oda-accent) 8%, transparent); }
.oda-rank-label {
  font-size: 12.5px;
  color: var(--fi-text, #2c3a49);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.oda-rank-track { position: relative; height: 10px; }
.oda-rank-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  gap: 2px;
}
.oda-rank-seg { flex-basis: 0; flex-shrink: 0; }
.oda-rank-seg:first-child { border-radius: 2px 0 0 2px; }
.oda-rank-seg:last-child { border-radius: 0 4px 4px 0; }
.oda-rank-seg:only-child { border-radius: 2px 4px 4px 2px; }
.oda-rank-seg--accent { background: var(--oda-accent); }
.oda-rank-seg--incident { background: var(--oda-c-incident); }
.oda-rank-seg--kudos { background: var(--oda-c-kudos); }
.oda-rank-seg--p-prev { background: var(--oda-p-prev); }
.oda-rank-seg--p-notprev { background: var(--oda-p-notprev); }
.oda-rank-seg--p-absence { background: var(--oda-p-absence); }
.oda-rank-seg--p-recog { background: var(--oda-p-recog); }
.oda-rank-seg--p-uncls { background: var(--oda-p-uncls); }
.oda-rank-count {
  font-size: 12px;
  font-weight: 600;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--fi-text-muted, #55636f);
}

/* ---- composition bands ---- */
.oda-band {
  display: flex;
  gap: 2px;
  height: 16px;
  margin: 2px 0 10px;
}
.oda-band-seg { flex-basis: 0; min-width: 3px; }
.oda-band-seg:hover { filter: brightness(1.08); }
.oda-band-seg:first-child { border-radius: 4px 0 0 4px; }
.oda-band-seg:last-child { border-radius: 0 4px 4px 0; }
.oda-band-seg:only-child { border-radius: 4px; }
.oda-band-seg--incident { background: var(--oda-c-incident); }
.oda-band-seg--kudos { background: var(--oda-c-kudos); }
.oda-band-seg--note { background: var(--oda-c-note); }
.oda-band-seg--s-new { background: var(--oda-s-new); }
.oda-band-seg--s-progress { background: var(--oda-s-progress); }
.oda-band-seg--s-waiting { background: var(--oda-s-waiting); }
.oda-band-seg--s-resolved { background: var(--oda-s-resolved); }
.oda-band-seg--v-high { background: var(--oda-v-high); }
.oda-band-seg--v-normal { background: var(--oda-v-normal); }
.oda-band-seg--v-low { background: var(--oda-v-low); }
.oda-band-seg--d-unspec { background: var(--oda-d-unspec); }
.oda-band-seg--src-staff { background: var(--oda-src-staff); }
.oda-band-seg--src-public { background: var(--oda-src-public); }
.oda-band-seg--src-imported { background: var(--oda-src-imported); }
.oda-band-legend { display: flex; flex-direction: column; gap: 3px; }
.oda-band-lrow {
  display: grid;
  grid-template-columns: 12px 1fr 52px 42px;
  gap: 8px;
  align-items: center;
  font-size: 12.5px;
  color: var(--fi-text, #2c3a49);
}
.oda-band-lrow--zero { opacity: 0.5; }
.oda-band-cnt {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.oda-band-pct {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--fi-text-muted, #6b7a8c);
}

@media (max-width: 700px) {
  .oda-rank-row { grid-template-columns: 110px 1fr 44px; }
}
</style>

<style>
/* midnight re-inks. global block (not scoped) because the data-fi-theme
   attribute lives on <html> and scoped selectors can't reach ancestors;
   :root prefix outweighs the scoped declarations it overrides. */
[data-fi-theme='midnight'] .oda-kpi-warn { color: #e08a4a; }
[data-fi-theme='midnight'] .oda-delta--bad { color: #e08a4a; }
/* --fi-success is a light-surface green with no midnight re-resolve; lift
   the good delta the same way --bad lifts (6.1:1 on the dark tile) */
[data-fi-theme='midnight'] .oda-delta--good { color: #46b881; }
/* dark-mode series palette: its own validated steps against the midnight
   card surface (#1a2836), not a lightened copy of the light values */
:root[data-fi-theme='midnight'] .oda-root {
  --oda-c-incident: #d07c2a;
  --oda-c-kudos: #27a075;
  --oda-c-note: #5f6e7d;
  --oda-c-avg: #cfdae5;
  --oda-s-new: #5f6e7d;
  --oda-s-progress: #4f95da;
  --oda-s-waiting: #bd8524;
  --oda-s-resolved: #27a075;
  --oda-v-high: #e46666;
  --oda-v-normal: #4f95da;
  --oda-v-low: #5f6e7d;
  --oda-d-unspec: #2f3f52;
  --oda-src-staff: #4f95da;
  --oda-src-public: #a06fd0;
  --oda-src-imported: #5f6e7d;
  --oda-p-prev: #e46666;
  --oda-p-notprev: #27a075;
  --oda-p-absence: #bd8524;
  --oda-p-recog: #4f95da;
  --oda-p-uncls: #5f6e7d;
  --oda-grid-line: #2c3b4a;
}
</style>
